import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const daysLeft = (deletedAt, retention) => {
  const d = new Date(deletedAt);
  if (isNaN(d)) return retention;
  const gone = new Date(d.getTime() + retention * 86400000);
  return Math.max(0, Math.ceil((gone - Date.now()) / 86400000));
};

const fmtDates = (td) => (Array.isArray(td) ? td.join(", ") : td || "");

export const TrashBin = ({ onChanged }) => {
  const [tours, setTours] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null); // tour to permanently delete
  const [emptyOpen, setEmptyOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listTrash();
      setTours(res.tours || []);
      setRetention(res.retention_days || 30);
    } catch {
      toast.error("Couldn't load Trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    try {
      await api.restoreGuest(t.id);
      setTours((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Tour restored");
      onChanged?.();
    } catch { toast.error("Couldn't restore tour"); }
  };

  const purge = async () => {
    const t = purgeTarget;
    if (!t) return;
    try {
      await api.purgeTrashItem(t.id);
      setTours((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Tour permanently deleted");
    } catch { toast.error("Delete failed"); }
    finally { setPurgeTarget(null); }
  };

  const emptyAll = async () => {
    try {
      const res = await api.emptyTrash();
      setTours([]);
      toast.success(`Emptied Trash — ${res.deleted} tour${res.deleted === 1 ? "" : "s"} deleted`);
    } catch { toast.error("Couldn't empty Trash"); }
    finally { setEmptyOpen(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-zinc-400" data-testid="trash-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Trash…
      </div>
    );
  }

  if (!tours.length) {
    return <p className="py-2 text-sm text-zinc-400" data-testid="trash-empty">Trash is empty. Deleted tours appear here for {retention} days before they're gone for good.</p>;
  }

  return (
    <div className="space-y-3" data-testid="trash-list">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-400">{tours.length} tour{tours.length === 1 ? "" : "s"} · auto-deleted after {retention} days</p>
        <button onClick={() => setEmptyOpen(true)} className="text-xs text-red-400/80 hover:text-red-400" data-testid="trash-empty-all-btn">Empty Trash</button>
      </div>
      <ul className="divide-y divide-zinc-800 overflow-hidden rounded-lg border border-zinc-800">
        {tours.map((t) => {
          const left = daysLeft(t.deleted_at, retention);
          return (
            <li key={t.id} className="flex items-center gap-3 bg-zinc-900/40 px-3 py-2.5" data-testid={`trash-row-${t.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-500 text-zinc-100" data-testid={`trash-title-${t.id}`}>{t.title || t.guest_name || "Tour"}</p>
                <p className="truncate text-xs text-zinc-500">
                  {fmtDates(t.tour_date)}{fmtDates(t.tour_date) ? " · " : ""}{left} day{left === 1 ? "" : "s"} left
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => restore(t)} className="h-8 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid={`trash-restore-${t.id}`}>
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
              </Button>
              <button onClick={() => setPurgeTarget(t)} className="p-1.5 text-red-400/70 hover:text-red-400" title="Delete permanently" data-testid={`trash-purge-${t.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          );
        })}
      </ul>

      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="trash-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Delete permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">
              <span className="font-500 text-zinc-100">{purgeTarget?.title || purgeTarget?.guest_name || "This tour"}</span> will be gone for good. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="trash-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={purge} className="bg-red-600 text-white hover:bg-red-700" data-testid="trash-purge-confirm">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={emptyOpen} onOpenChange={setEmptyOpen}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="trash-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Empty Trash?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">All {tours.length} tour{tours.length === 1 ? "" : "s"} in Trash will be permanently deleted. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="trash-empty-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={emptyAll} className="bg-red-600 text-white hover:bg-red-700" data-testid="trash-empty-confirm">Empty Trash</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
